import tinycolor from '@kittl/tinycolor2';
import { rgba_to_lab, diff } from 'color-diff';

import { NONE_COLOR } from './constants';
import { Color } from './types';

type EmptyColor = null | undefined | '' | 'none';

export const isEmptyColor = (color: Color): color is EmptyColor => {
  return (
    color === undefined || color === null || color === '' || color === 'none'
  );
};

export const ensureColor = <T extends Color>(
  color: T
): NonNullable<T> | string => {
  // color set to 'null' means no color
  if (color === undefined || color === null || color === 'null') {
    return NONE_COLOR;
  }

  return color;
};

export const formatColor = (
  color: string,
  format: Parameters<tinycolor.Instance['toString']>[0]
): string => {
  return tinycolor(color).toString(format);
};

/**
 * test whether a color is valid for color comparison
 * only strings are valid for comparison
 */
const isValidColorForComparison = (color: Color): color is string => {
  if (isEmptyColor(color)) {
    return false;
  }

  if (typeof color === 'object') {
    // comparisons involving patterns and gradients always yield false
    return false;
  }

  return true;
};

export const sameColors = (colorA?: Color, colorB?: Color): boolean => {
  if (
    !isValidColorForComparison(colorA) ||
    !isValidColorForComparison(colorB)
  ) {
    return false;
  }

  return tinycolor.equals(tinycolor(colorA), tinycolor(colorB));
};

/**
 * Get the Euclidean distance between two colors using the CIEDE2000 formula
 * See https://en.wikipedia.org/wiki/Color_difference#CIEDE2000
 * @param color1 - The first color
 * @param color2 - The second color
 * @returns The distance between the two colors
 */
export const getColorDistance = (color1: string, color2: string): number => {
  const rgb1 = tinycolor(color1);
  const rgb2 = tinycolor(color2);
  const lab1 = rgba_to_lab({
    R: rgb1.toRgb().r,
    G: rgb1.toRgb().g,
    B: rgb1.toRgb().b,
    A: rgb1.toRgb().a,
  });
  const lab2 = rgba_to_lab({
    R: rgb2.toRgb().r,
    G: rgb2.toRgb().g,
    B: rgb2.toRgb().b,
    A: rgb2.toRgb().a,
  });
  return diff(lab1, lab2);
};

/**
 * Check if two colors are similar
 * @param color1 - The first color
 * @param color2 - The second color
 * @returns Whether the colors are similar
 */
export const checkIfSimilarColors = (
  color1: string,
  color2: string,
  threshold: number
): { isSimilar: boolean; distance: number } => {
  const distance = getColorDistance(color1, color2);
  const isSimilar = distance < threshold;
  return {
    isSimilar,
    distance,
  };
};
